import { useCallback, useEffect, useState } from 'react'

import { STORAGE_KEYS } from '@/shared/constants'

export interface AppGlobalVariableValue {
  name: string
  value: string
}

export interface AppGlobalVariablesClient {
  getValues: () => AppGlobalVariableValue[]
  getValue: (name: string) => string | undefined
  setValue: (name: string, value: string) => void
  clearValue: (name: string) => void
  setValues: (values: AppGlobalVariableValue[]) => void
  subscribe: (callback: (values: AppGlobalVariableValue[]) => void) => () => void
  close: () => void
}

/**
 * App Global Variables Client
 *
 * @note this is a singleton class that is used to manage global variables for the app
 */
class GlobalVariablesClient implements AppGlobalVariablesClient {
  appID: string = ''
  values: AppGlobalVariableValue[] = []
  subscribers: Set<(values: AppGlobalVariableValue[]) => void> = new Set()

  constructor(appID: string) {
    this.appID = appID

    // get values from local storage
    try {
      this.values = JSON.parse(localStorage.getItem(`${STORAGE_KEYS.APP_GLOBAL_VARIABLES}:${appID}`) || '[]')
    } catch (err) {
      console.error('Error parsing global variables: ', err)
    }
  }

  subscribe = (callback: (values: AppGlobalVariableValue[]) => void): (() => void) => {
    this.subscribers.add(callback)
    return () => {
      this.subscribers.delete(callback)
    }
  }

  /**
   * @internal notify subscribers
   */
  private notifySubscribers = () => {
    this.subscribers.forEach((callback) => callback(this.values))
  }

  private syncUpdate = () => {
    localStorage.setItem(`${STORAGE_KEYS.APP_GLOBAL_VARIABLES}:${this.appID}`, JSON.stringify(this.values))
    this.notifySubscribers()
  }

  getValues = () => {
    return this.values
  }

  getValue = (name: string) => {
    return this.values.find((v) => v.name === name)?.value
  }

  setValue = (name: string, value: string) => {
    // check if value is already set
    if (this.values.find((v) => v.name === name)) {
      this.values = this.values.map((v) => (v.name === name ? { ...v, value } : v))
    } else {
      this.values = [...this.values, { name, value }]
    }
    this.syncUpdate()
  }

  clearValue = (name: string) => {
    this.values = this.values.filter((v) => v.name !== name)
    this.syncUpdate()
  }

  setValues = (values: AppGlobalVariableValue[]) => {
    this.values = values
    this.syncUpdate()
  }

  close = () => {
    this.values = []
  }
}

/**
 * Global Variable client instance, can be accessed globally
 * it should be initialized after the app client is loaded.
 */
export let globalVariableClient: GlobalVariablesClient | null = null

/**
 * Initialize global variable client instance
 */
export function openGlobalVariablesClient(appID: string) {
  if (!!globalVariableClient) return
  globalVariableClient = new GlobalVariablesClient(appID)
}

/**
 * Close global variable client instance
 */
export function closeGlobalVariablesClient() {
  if (globalVariableClient) {
    globalVariableClient.close()
    globalVariableClient = null
  }
}

/**
 * Global variable client hook, used within react components tree
 */
export function useGlobalVariableValues() {
  const [globalVariables, setGlobalVariables] = useState<AppGlobalVariableValue[]>([])

  useEffect(() => {
    if (!window.appID || !globalVariableClient) return

    setGlobalVariables(globalVariableClient.values)

    const unsubscribe = globalVariableClient.subscribe((newValues) => {
      setGlobalVariables(newValues)
    })

    return () => {
      unsubscribe()
    }
  }, [])

  const getVariableValue = useCallback(
    (name: string) => {
      return globalVariables.find((v) => v.name === name)?.value
    },
    [globalVariables]
  )

  return {
    globalVariables,
    setGlobalVariables,
    getVariableValue,
  }
}
